// hooks/useAdminLogin.ts
import { useState, ChangeEvent, FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { isFormValid, isValidEmail } from "../../utils/authHelpers";
import { useAuth } from "../../context/AuthContext";
import { BackendUserData } from "../../types";
import { getApiUrl } from "../../utils/api";
import devLogger from "../../utils/devLogger";

export const useAdminLogin = (): UseAdminLoginReturn => {
  const { login } = useAuth();
  const navigate = useNavigate();

  const [loginDetails, setLoginDetails] = useState<LoginDetailsProps>({
    email: undefined,
    password: undefined,
  });

  const [errors, setErrors] = useState<{ email?: string; password?: string }>({
    email: undefined,
    password: undefined,
  });

  const [loading, setLoading] = useState(false);

  const [loginError, setLoginError] = useState<LoginError>({
    error: false,
    message: "",
  });

  const handleDetailsChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setLoginDetails((prev) => ({ ...prev, [name]: value }));

    if (name === "email") {
      setErrors((prev) => ({
        ...prev,
        email:
          value && !isValidEmail(value) ? "Invalid email format" : undefined,
      }));
    } else if (name === "password") {
      setErrors((prev) => ({
        ...prev,
        password:
          !value || value.length < 6
            ? "Password should be at least 6 characters"
            : undefined,
      }));
    }
  };

  const handleLogin = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setLoginError({
      error: false,
      message: undefined,
    });

    try {
      const response = await fetch(getApiUrl("/auth/login"), {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ ...loginDetails, userType: "admin" }),
      });

      // devLogger.log("🚀 ~ handleLogin ~ response:", response);

      const data: { data: BackendUserData; message: string } =
        await response.json();

      if (!response.ok) {
        setLoginError({
          error: true,
          message: data.message || "Login failed. Please try again.",
        });
      } else {
        login(data.data);

        // Redirect to dashboard
        navigate("/admin/dashboard");
      }
    } catch (error) {
      setLoginError({
        error: true,
        message: "An error occurred. Please try again later.",
      });

      devLogger.error("Login error:", error);
    } finally {
      setLoading(false);
    }
  };

  return {
    loginDetails,
    errors,
    loading,
    loginError,
    setLoginError,
    handleDetailsChange,
    handleLogin,
    isFormValid: isFormValid(loginDetails, errors),
  };
};

type LoginDetailsProps = {
  email: string | undefined;
  password: string | undefined;
};

type LoginError = {
  error: boolean;
  message?: string;
};

interface UseAdminLoginReturn {
  loading: boolean;
  errors: { email?: string; password?: string };
  loginError: LoginError;
  loginDetails: LoginDetailsProps;
  isFormValid: boolean;
  setLoginError: (loginError: LoginError) => void;
  handleDetailsChange: (e: ChangeEvent<HTMLInputElement>) => void;
  handleLogin: (e: FormEvent<HTMLFormElement>) => Promise<void>;
}
